import { observable, action, runInAction } from "mobx";
import agent from "../agent";
import userStore from "./userStore";

class VenueHgSuperPayStore {
    @observable employerModel = null;
    @observable selectedVenueId = null;
    @observable accessibleVenues = [];
    @observable organisationId = null;
    @observable isLoading = false;
    @observable venueCustomisationMap = {};

    @action
    async loadForOrganisation(orgId) {
        this.organisationId = orgId;
        this.isLoading = true;
        try {
            const [org, venuesResp] = await Promise.all([
                agent.Organisation.load(orgId),
                agent.Venue.getUserVenuesBasedOnPermission(null, null, true, null, null)
            ]);
            runInAction(() => {
                this.employerModel = org.hgSuperPaySetupLevel === 1 ? "group" : org.hgSuperPaySetupLevel === 2 ? "per-venue" : null;
                const venueList = Array.isArray(venuesResp)
                    ? venuesResp
                    : (Array.isArray(venuesResp && venuesResp.data) ? venuesResp.data : []);

                // Restrict to venues that belong to this organisation.
                // Strategy 1: org response includes a venues array with IDs/objects.
                let filtered = [];
                const orgVenues = org.venues || org.venueIds || [];
                if (orgVenues.length > 0) {
                    const orgVenueIdSet = new Set(
                        orgVenues.map(v => (typeof v === "object" ? (v.id || v.profileId) : v))
                    );
                    filtered = venueList.filter(v => orgVenueIdSet.has(v.id || v.profileId));
                }
                // Strategy 2: venue objects carry an org reference field.
                if (filtered.length === 0) {
                    filtered = venueList.filter(v =>
                        v.organisationId === orgId ||
                        v.organisationProfileId === orgId ||
                        v.parentOrganisationId === orgId
                    );
                }
                // If no org-scoped venues resolved, keep list empty (safer than leaking other-org venues).
                this.accessibleVenues = filtered.length > 0 ? filtered : [];

                // Always start with Default Setting on first load
                if (!this.selectedVenueId) {
                    this.selectedVenueId = "__default__";
                }
            });
            return org;
        } catch (e) {
            runInAction(() => {
                this.employerModel = null;
                this.accessibleVenues = [];
            });
            throw e;
        } finally {
            runInAction(() => { this.isLoading = false; });
        }
    }

    @action
    setSelectedVenue(venueId) {
        this.selectedVenueId = venueId;
    }

    @action
    async checkVenueCustomisation(venueId) {
        this.venueCustomisationMap[venueId] = "loading";
        try {
            const response = await agent.HgSuperPay.checkVenueCustomisation(venueId);
            const isCustomised = !!(response && response.isCustomised);
            runInAction(() => { this.venueCustomisationMap[venueId] = isCustomised; });
            return { isCustomised };
        } catch (e) {
            // Restore to unknown state and re-throw — callers must not treat a failed
            // check as an authoritative "uses default" value.
            runInAction(() => { this.venueCustomisationMap[venueId] = "error"; });
            throw e;
        }
    }

    @action
    async setVenueCustomisation(venueId, isCustomised) {
        const previous = this.venueCustomisationMap[venueId];
        try {
            const response = await agent.HgSuperPay.setVenueCustomisation(venueId, isCustomised);
            const result = response && response.isCustomised !== undefined ? !!response.isCustomised : isCustomised;
            runInAction(() => { this.venueCustomisationMap[venueId] = result; });
            return { isCustomised: result };
        } catch (e) {
            // Restore previous state and re-throw so callers can handle the failure
            runInAction(() => { this.venueCustomisationMap[venueId] = previous === "loading" ? false : previous; });
            throw e;
        }
    }

    @action
    async switchEmployerModel(orgId, newModel) {
        await agent.Organisation.updateEmployerModel(orgId, newModel);
        runInAction(() => {
            this.employerModel = newModel;
        });
    }

    @action
    reset() {
        this.employerModel = null;
        this.selectedVenueId = null;
        this.accessibleVenues = [];
        this.organisationId = null;
        this.isLoading = false;
        this.venueCustomisationMap = {};
    }
}

export default new VenueHgSuperPayStore();
